import React from 'react';

interface ContentContainerProps {
  children: React.ReactNode;
  isLive?: boolean;
  toggleMode?: 'static' | 'auto' | 'live';
  timeSinceUpdate?: number;
  refreshCycleSeconds?: number;
  isRefreshing?: boolean;
}

export const ContentContainer: React.FC<ContentContainerProps> = ({
  children,
  isLive = false,
  toggleMode = 'static',
  timeSinceUpdate = 0,
  refreshCycleSeconds = 15,
  isRefreshing = false,
}) => {
  // Show pulse animation only in Live mode (not Auto mode)
  const isLiveMode = isLive && toggleMode === 'live';
  const isAutoMode = isLive && toggleMode === 'auto';

  // Calculate progress bar width for Auto mode
  const progressWidth = isAutoMode
    ? isRefreshing
      ? 0
      : Math.max(0, 100 - (timeSinceUpdate / refreshCycleSeconds) * 100)
    : 0;

  const BoltIcon = () => (
    <svg
      viewBox='0 0 24 24'
      width='32'
      height='32'
      fill='currentColor'
      className='text-yellow-400 animate-pulse'
    >
      <path d='M13 3L4 14h7v7l9-11h-7V3z' />
    </svg>
  );

  return (
    <>
      {/* Inject CSS keyframes */}
      <style>
        {`
          @keyframes live-pulse {
            0% {
              box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
            }
            70% {
              box-shadow: 0 0 0 10px rgba(255, 255, 255, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(255, 255, 255, 0);
            }
          }

          /* Dark mode pulse animation */
          @keyframes live-pulse-dark {
            0% {
              box-shadow: 0 0 0 0 rgba(0, 0, 0, 0.4);
            }
            70% {
              box-shadow: 0 0 0 10px rgba(0, 0, 0, 0);
            }
            100% {
              box-shadow: 0 0 0 0 rgba(0, 0, 0, 0);
            }
          }

          /* Base live pulse animation class */
          .live-pulse-animation {
            animation: live-pulse 2s infinite;
          }

          /* Apply dark pulse animation in dark mode */
          html.dark .live-pulse-animation {
            animation: live-pulse-dark 2s infinite !important;
          }

          .refresh-progress-bar {
            position: relative;
            width: 100%;
            height: 0;
            overflow: visible;
            z-index: 5;
          }

          .refresh-progress-bar .progress {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            height: 6px;
            background: #FFFFFF;
            /* background: linear-gradient(90deg, #2A7B9B, #57C785, #EDDD53); */
            box-shadow: 0 0 8px rgba(255, 255, 255, 0.4);
            /* box-shadow: 0 0 8px rgba(42, 123, 155, 0.4); */
            width: 100%;
            transition: width 1s linear;
            border-top-left-radius: 8px;
            border-top-right-radius: 8px;
          }

          /* Dark mode support for progress bar */
          html.dark .refresh-progress-bar .progress {
            background: #000000;
            box-shadow: 0 0 8px rgba(0, 0, 0, 0.4);
          }
        `}
      </style>
      <div
        className={`relative bg-surface-light/80 dark:bg-surface-dark/80 backdrop-blur-sm rounded-lg border border-border-light dark:border-border-dark ${
          isLiveMode ? 'live-pulse-animation' : ''
        }`}
        style={isLiveMode ? { position: 'relative' } : {}}
      >
        {/* Auto Mode Progress Bar */}
        {isAutoMode && (
          <div className='refresh-progress-bar'>
            <div
              className='progress'
              style={{
                width: `${progressWidth}%`,
                transition: isRefreshing ? 'none' : 'width 1s linear',
              }}
            ></div>
          </div>
        )}

        <div className='relative z-10 p-6 space-y-6'>
          {/* Live Mode Lightning Icon - inside container */}
          {isLiveMode && (
            <div className='absolute top-4 right-4 z-20'>
              <BoltIcon />
            </div>
          )}
          {children}
        </div>
      </div>
    </>
  );
};
